import { NamedError } from '../utils/Errors';

export class LoadTranscriptionStringsError extends NamedError<LoadTranscriptionStringsErrors> {}

export enum LoadTranscriptionStringsErrors {
    FailedToLoadStrings = 'FailedToLoadStrings',
    InvalidInput = 'InvalidInput'
}

export const LOAD_TRANSCRIPTION_STRINGS_FAILURE_MESSAGE = "Hmmm...something went wrong. Try again later.";

// List of all language codes we support. These come from the directories in node_modules\@ms\office-online-strings.
const supportedLanguages = ['af-ZA', 'am-ET', 'ar-SA', 'as-IN', 'az-Latn-AZ', 'be-BY', 'bg-BG', 'bn-BD', 'bn-IN', 'bs-Latn-BA', 'ca-ES', 'ca-Es-VALENCIA', 'chr-Cher-US', 'cs-CZ', 'cy-GB', 'da-DK', 'de-DE', 'el-GR', 'en-GB', 'en-US', 'es-ES', 'es-MX', 'et-EE', 'eu-ES', 'fa-IR', 'fi-FI', 'fil-PH', 'fr-CA', 'fr-FR', 'ga-IE', 'gd-gb', 'gl-ES', 'gu-IN', 'ha-Latn-NG', 'he-IL', 'hi-IN', 'hr-HR', 'hu-HU', 'hy-AM', 'id-ID', 'is-IS', 'it-IT', 'ja-JP', 'ka-GE', 'kk-KZ', 'km-KH', 'kn-IN', 'ko-KR', 'kok-IN', 'ky-KG', 'lb-LU', 'lib', 'lo-LA', 'lt-LT', 'lv-LV', 'mi-NZ', 'mk-MK', 'ml-IN', 'mn-MN', 'mr-IN', 'ms-MY', 'mt-MT', 'nb-NO', 'ne-NP', 'nl-NL', 'nn-NO', 'or-IN', 'pa-IN', 'pl-PL', 'prs-AF', 'pt-BR', 'pt-PT', 'quz-PE', 'ro-RO', 'ru-RU', 'sd-Arab-PK', 'si-LK', 'sk-SK', 'sl-SI', 'sq-AL', 'sr-Cyrl-BA', 'sr-Cyrl-RS', 'sr-Latn-RS', 'sv-SE', 'sw-KE', 'ta-IN', 'te-IN', 'th-TH', 'tk-TM', 'tr-TR', 'tt-RU', 'ug-CN', 'uk-UA', 'ur-PK', 'uz-Latn-UZ', 'vi-VN', 'zh-CN', 'zh-TW'];

// This function is a last-resort we use to map an unknown language code to a default one.
// Default language codes are whatever language code for the required language we first find in the supportedLanguages array.
// This is a workaround while we get better language codes from WAC (VSO:4171604 tracks this work).
function GetDefaultLanguageCode(languageCode: string): string {
  const languageCodeParts = languageCode.split('-');

  for (let i = 0; i < supportedLanguages.length; i++) {
    const supportedLanguageCode = supportedLanguages[i];
    if (languageCodeParts[0] == supportedLanguageCode.split('-')[0])
      return supportedLanguageCode;
  }

  return languageCode;
}

// This function maps exotic language codes to language codes we support.
// Mapping is done manually so this can be thought of as "sensible, human-curated defaults" (e.g. en-CA is mapped to en-US instead of en-GB).
function GetSupportedLanguageCode(navigatorLanguage: string): string {
  switch (navigatorLanguage) {
    case 'es-US':
      return 'es-MX';
    case 'en':
      return 'en-US';
    case 'sv':
      return 'sv-SE';
    case 'it':
      return 'it-IT';
    case 'es-419':
      return 'es-MX';
    case 'en-CA':
      return 'en-US';
    case 'th':
      return 'th-TH';
    case 'de':
      return 'de-DE';
    case 'es-PE':
      return 'es-MX';
    default:
      return navigatorLanguage;
  }
}

function LoadScriptResourceAsync(scriptUrl: string): Promise<void> {
  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = scriptUrl;

  const loadPromise: Promise<void> = new Promise((resolve, reject) => {
    script.onload = () => {
      resolve();
    };

    script.onerror = script.onabort = () => {
      reject();
    }
  });

  document.head.appendChild(script);
  return loadPromise;
}

export async function LoadTranscriptionStringsAsync(): Promise<void> {
  if (navigator.language == null)
    throw new LoadTranscriptionStringsError(LoadTranscriptionStringsErrors.InvalidInput, "navigator.language is undefined or null");

  // Try to map the user's language code to a known one.
  let userLang = GetSupportedLanguageCode(navigator.language);

  if (supportedLanguages.indexOf(userLang) == -1) {
    // If we still don't have a supported language code, try to find one we support for the required language.
    userLang = GetDefaultLanguageCode(userLang);
  }

  const stringDictionaryJSName = 'transcription-strings.min.js';
  const stringHostPath = 'office-online-strings';
  const stringDictionaryJSUrl = stringHostPath + '/' + userLang + '/' + stringDictionaryJSName;

  try {
    return LoadScriptResourceAsync(stringDictionaryJSUrl);
  } catch (e) {
    throw new LoadTranscriptionStringsError(LoadTranscriptionStringsErrors.FailedToLoadStrings, "Failed to load strings with mapped language = " + userLang + " and navigator.language = " + navigator.language);
  }
}